<template>
  <v-card class="pa-4" outlined elevation="8">
    <v-card-text>
      <v-data-table-server
        :headers="headers"
        :items="orderOneStrykerItems"
        :items-length="totalItems"
        :loading="loadingDatatables"
        :search="search"
        density="compact"
        class="text-caption"
        @update:sortBy="toSort"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-toolbar-title>
              <v-icon
                color="medium-emphasis"
                :icon="mdiPackageVariantClosed"
                size="x-small"
                start
              ></v-icon>

              Commande Programme One
            </v-toolbar-title>

            <v-chip
              v-if="selectedYear"
              class="mx-3"
              color="primary"
              variant="outlined"
            >
              Année contractuelle : {{ selectedYear }} (16 jan
              {{ selectedYear }} - 15 jan {{ selectedYear + 1 }})
            </v-chip>

            <v-select
              v-model="selectedYear"
              :items="availableYears"
              label="Année"
              variant="outlined"
              hide-details
              density="compact"
              style="max-width: 150px"
              class="mr-3"
              clearable
              @update:model-value="onYearChange"
            ></v-select>

            <v-text-field
              v-model="search"
              label="Rechercher"
              :prepend-inner-icon="mdiMagnify"
              variant="outlined"
              hide-details
              @update:modelValue="toSearchBack"
              single-line
              class="pr-5"
            ></v-text-field>

            <v-btn
              class="me-2"
              :icon="mdiChartBoxMultipleOutline"
              rounded="lg"
              border
              @click="showStats"
            ></v-btn>

            <v-btn
              :icon="mdiFileDownloadOutline"
              rounded="lg"
              border
              @click="exportExcelOrderOne"
            ></v-btn>
          </v-toolbar>
        </template>

        <template v-slot:item.orderDate="{ item }">
          <span>{{ new Date(item.orderDate).toLocaleDateString() }}</span>
        </template>

        <template v-slot:item.totalHT="{ item }">
          <span>{{ (item.unitPrice * item.quantity).toFixed(2) }}</span>
        </template>

        <template v-slot:item.totalCost="{ item }">
          <span>{{ (item.currentCost * item.quantity).toFixed(2) }}</span>
        </template>

        <template v-slot:item.savings="{ item }">
          <span>
            {{
              (() => {
                // Conversion forcée en float, retourne 0 si la conversion échoue
                const totalHT =
                  parseFloat(item.unitPrice) * parseFloat(item.quantity);
                const totalCost =
                  parseFloat(item.currentCost) * parseFloat(item.quantity);
                if (!isNaN(totalHT) && !isNaN(totalCost)) {
                  return (totalHT - totalCost).toFixed(2);
                }
                return "0.00";
              })()
            }}
          </span>
        </template>

        <template v-slot:item.margin="{ item }">
          <span>
            {{
              (() => {
                const totalHT =
                  parseFloat(item.unitPrice) * parseFloat(item.quantity);
                const totalCost =
                  parseFloat(item.currentCost) * parseFloat(item.quantity);
                if (!isNaN(totalHT) && totalHT !== 0 && !isNaN(totalCost)) {
                  return (((totalHT - totalCost) / totalHT) * 100).toFixed(2);
                }
                return "0.00";
              })()
            }}%
          </span>
        </template>

        <template v-slot:bottom>
          <pagination-dynamic :store="'kpi'"></pagination-dynamic>
        </template>
      </v-data-table-server>
    </v-card-text>
    <order-one-stryker-stats-dialog
      :dialog="statsDialog"
      @close="closeDialog"
    ></order-one-stryker-stats-dialog>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import {
  mdiChartBoxMultipleOutline,
  mdiFileTableBoxMultipleOutline,
  mdiMagnify,
  mdiPackageVariantClosed,
  mdiFileDownloadOutline,
} from "@mdi/js";
import PaginationDynamic from "@/components/PaginationDynamic.vue";
import paginate from "@/mixins/paginations";
import contractYear from "@/mixins/contractYear";
import OrderOneStrykerStatsDialog from "@/components/kpi/OrderOneStrykerStatsDialog.vue";

export default {
  name: "OrderOneStrykerKpi",
  mixins: [paginate, contractYear],
  components: {
    PaginationDynamic,
    OrderOneStrykerStatsDialog,
  },
  data() {
    return {
      mdiChartBoxMultipleOutline,
      mdiFileTableBoxMultipleOutline,
      mdiMagnify,
      mdiPackageVariantClosed,
      mdiFileDownloadOutline,
      type: "table",
      statsDialog: false,
      selectedYear: null,
      availableYears: [],
    };
  },
  mounted() {
    this.initializeYears();
  },
  computed: {
    ...mapState({
      orderOneStrykerItems: (state) => state.kpi.orderOneStrykerItems,
      loadingDatatables: (state) => state.loadingTables,
      totalItems: (state) => state.paginations.totalCount,
    }),
    pageCount() {
      return Math.ceil(this.orderOneStrykerItems.length / this.itemsPerPage);
    },
    search: {
      get() {
        return this.$store.state.paginations.search;
      },
      set(value) {
        this.$store.commit("paginations/setSearch", value);
      },
    },
    headers() {
      return [
        { title: "Numéro client", key: "customer.number", minWidth: "150px" },
        {
          title: "Nom client",
          key: "customer.name",
          align: "center",
          minWidth: "200px",
        },
        {
          title: "Numéro de commande",
          value: "orderNumber",
          align: "center",
          minWidth: "200px",
        },
        {
          title: "Franchise Groupe",
          key: "franchiseGroup",
          align: "center",
          minWidth: "200px",
        },
        {
          title: "Franchise",
          key: "franchise",
          align: "center",
          minWidth: "150px",
        },
        {
          title: "Sub franchise",
          key: "subFranchise",
          align: "center",
          minWidth: "150px",
        },
        {
          title: "Categorie",
          value: "sapgCustom.name",
          align: "center",
          minWidth: "150px",
        },
        {
          title: "Reference",
          value: "reference",
          align: "center",
        },
        {
          title: "Designation",
          value: "description",
          align: "center",
        },
        {
          title: "Conditionnement",
          value: "gimPackContent",
          align: "center",
        },
        {
          title: "PUHT Tarif",
          value: "unitPrice",
          align: "center",
          minWidth: "100px",
        },
        {
          title: "Quantite",
          value: "quantity",
          align: "center",
        },
        {
          title: "Total HT",
          value: "totalHT",
          align: "center",
          minWidth: "100px",
        },
        {
          title: "Coût unitaire",
          value: "currentCost", // add this field into database
          align: "center",
          minWidth: "130px",
        },
        {
          title: "Coût total",
          value: "totalCost",
          align: "center",
          minWidth: "100px",
        },
        {
          title: "Economies",
          value: "savings",
          align: "center",
        },
        {
          title: "Marge",
          value: "margin",
          align: "center",
        },
        {
          title: "Date de commande",
          value: "orderDate",
          align: "center",
          minWidth: "150px",
        },
      ];
    },
  },
  methods: {
    /**
     * Initialise la liste des années disponibles
     * À partir de 2025 jusqu'à l'année contractuelle actuelle
     */
    initializeYears() {
      const currentYear = this.currentYear;
      const startYear = 2025;
      const years = [];
      for (let i = startYear; i <= currentYear; i++) {
        years.push(i);
      }
      this.availableYears = years;
    },
    /**
     * Appelée lorsque l'utilisateur change l'année sélectionnée
     */
    onYearChange() {
      // Stocker l'année sélectionnée dans le store
      this.$store.commit("kpi/setSelectedContractYear", this.selectedYear);

      // Réinitialiser la pagination et relancer la requête
      this.$store.commit("paginations/setCurrentPage", 1);

      let paginationParams = "?PageNumber=1&PageSize=10";
      if (this.selectedYear) {
        paginationParams += `&ContractYear=${this.selectedYear}`;
      }
      if (this.search) {
        paginationParams += `&Search=${this.search}`;
      }

      const data = {
        text: paginationParams,
      };

      this.$store.commit("setOverlay", true);
      this.$store
        .dispatch("kpi/fetchOrderOneStrykerStatistics", data)
        .then((response) => {
          this.$store.commit("setOverlay", false);
          if (response && response.headers) {
            this.$store.commit(
              "paginations/setItems",
              JSON.parse(response.headers.get("X-Pagination")),
            );
          }
        })
        .catch(() => {
          this.$store.commit("setOverlay", false);
          this.$store.dispatch("showSnackbar", {
            message: "Erreur lors du chargement des données.",
            color: "error",
          });
        });
    },
    exportExcelOrderOne() {
      this.$store.commit("setOverlay", true);

      const data = {
        contractYear: this.selectedYear,
        search: this.search || null,
      };
      this.$store
        .dispatch("kpi/exportExcelOrderOne", data)
        .then((response) => {
          this.$store.commit("setOverlay", false);
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          // Create a link element
          const link = document.createElement("a");
          // Set the download attribute with the desired file name
          link.download = "OrderProgrammeOne.xlsx";

          // Create a URL for the Blob and set it as the link's href
          link.href = window.URL.createObjectURL(blob);

          // Append the link to the document
          document.body.appendChild(link);

          // Trigger a click on the link to start the download
          link.click();

          // Remove the link from the document
          document.body.removeChild(link);
        })
        .catch((error) => {
          if (error.status == 400) {
            this.$store.commit("setOverlay", false);
            this.$store.dispatch("showSnackbar", {
              message: "Action non autorisée.",
              color: "error",
            });
          } else {
            this.$store.commit("setOverlay", false);
            this.$store.dispatch("showSnackbar", {
              message: this.$t("alert.error.export-excel"),
              color: "error",
            });
          }
        });
    },
    closeDialog() {
      this.statsDialog = false;
    },
    showStats() {
      this.$store.dispatch("kpi/fetchGimCustoms").then(() => {
        this.statsDialog = true;
      });
    },
    toSort(item) {
      this.sortBy(item, "kpi");
    },
    toSearchBack(value, time = 500) {
      this.searchBack(value, time, "kpi");
    },
    changeView() {
      this.type = this.type === "table" ? "chart" : "table";
    },
  },
};
</script>
